import React, { useEffect, useState, useContext } from "react";
import { useLocation, useParams, Outlet, useNavigate } from "react-router-dom";
import {
	ContentContainer,
	ContentGroup,
	Detail,
	createDialog,
	QuestionDialog
} from "coaz-ui";
import {
	PiGraduationCapLight,
	PiStudentLight,
	PiPercentLight,
} from "react-icons/pi";
import { GlobalContext } from "../contexts/GlobalContext";
import client from "../client";
import ContentColumn from "./ContentColumn";
import ContentRow from "./ContentRow";
import ContentRowItem from "./ContentRowItem";
import ResponsiveChildren from "./ResponsiveChildren";
import Card from "./Card";
import AddCpdStudent from "./AddCpdStudent";


const Student = () => {
	const { version, setVersion } = useContext(GlobalContext);
	const [student, setStudent] = useState(null);
	const [cpdStudents, setCpdStudents] = useState([]);
	const [availableCpds, setAvailableCpds] = useState([]);
	const [loading, setLoading] = useState(true);
	const [user,setUser] = useState(null);
	const { programId, cpdId } = useParams();
	const path = useLocation().pathname;
	const { request, getCurrent, errorMessage } = client;

	const getstudent = async () => {
		if (!programId) return;

		if (!user) return;

		try {
			const response = await request("GET", "students", null, {
				userReference:user.ref.value,
				programId: programId,
			});

			if (response) {
				setStudent(response);
			} else {
				setStudent(null);
			}
		} catch (error) {
			console.log(error);
			setStudent(null);
		}
	};

	const getCpdStudents = async () => {
		if (!programId) return;

		if (!user) return;

		try {
			const response = await request("GET", "cpd-students/by-student", null, {
				userReference:user.ref.value,
				programId: programId,
			});

			if (response) {
				setCpdStudents(response);
			} else {
				setCpdStudents(null);
			}
		} catch (error) {
			console.log(error);
			setCpdStudents(null);
		}
	};

	// need to get endpont name for getting availabel cpdes and enrolled cpds for a program.
	const getAvailableCpds = async () => {

		if (!user) return;

		try {
			const response = await request("GET", "cpd-students/available", null, {
				userReference:user.ref.value,
				programId: programId,
			});
			setAvailableCpds(response);
		} catch (error) {
			console.log(error);
			setAvailableCpds([]);
		}
	};

	const navigate = useNavigate();

	const onOpen = (e,id) => {
		e.preventDefault();
		e.stopPropagation();
		if(!id) return;
		navigate(`${path}/_cpd/${id}`)
	};

	const onEnroll = (e,id) => {
		e.preventDefault();
		e.stopPropagation();
		if(!id) return;
		createDialog({
			title: "Enroll",
			submitText: "Enroll",
			Component: AddCpdStudent,
			params: {
				cpdId:id
			},
			version: version,
			setVersion: setVersion,
		});
	};

	const onUnenroll = (e,id) => {
		e.preventDefault();
		e.stopPropagation();
		if(!id) return;
		createDialog({
			title: "Unenroll",
			submitText: "Yes",
			cancelText: "No",
			Component: QuestionDialog,
			params: {
				message: `Are you sure you want to unenroll?`,
				submit: async () => {
					try {
						await request("DELETE", "cpd-students", null, {
							cpdId:id,
							userReference:user.ref.value,
							programId: id,
						});
						return { message: "Successful", success: true };
					} catch (error) {
						return { message: errorMessage(error) };
					}
				},
			},
			version: version,
			setVersion: setVersion,
		});
	};

	const load = async () => {
		setLoading(true);
		await getstudent()
		await getCpdStudents();
		await getAvailableCpds();
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, [user])

	useEffect(() => {
		(async () => {
			try {
				const curr = await getCurrent();

				if (curr && curr.user) {
					setUser(curr.user);
				} else {
					setUser(null);
				}
			} catch(error) {
				console.log(error);
				setUser(null);
			}
		})()
	}, [programId, version]);

	if (programId && cpdId ) return <Outlet />;

	return (
		<ContentContainer Icon={PiGraduationCapLight} text={student?.program?.name ?? ""} scrollable={true} loading={loading}>
			{student && student.program && student.user &&
			<ContentColumn>
				<ContentRow>
					<ContentRowItem title={"Details"}>
						<div className="flex flex-col w-full h-auto p-2 space-y-2">
							<Detail Icon={PiGraduationCapLight} label={"Name"} value={student.program.name}/>
							<Detail 
								Icon={PiStudentLight} 
								label={"Student"} 
								value={`${student.user.firstname} ${student.user.lastname}`}/>
							<Detail 
								Icon={PiPercentLight} 
								label={"Completion"} 
								value={`${student.completionPercentage != null?student.completionPercentage:"0"}%`}/>
						</div>
					</ContentRowItem>
				</ContentRow>
				<ContentRow>
					<ContentGroup title={"Enrolled Cpds"}>
						<ResponsiveChildren minGap={24}>
						{cpdStudents.map((cpd, index) => (
							<Card
								key={cpd?.cpd?.ref?.id ?? index}
								id={cpd?.cpd?.ref?.id ?? index}
								heading={cpd?.cpd?.name}
								body={cpd?.cpd?.description}
								image={`${process.env.PUBLIC_URL}/images/cpd-student.jpg`}
								onOpen={onOpen}
								actionText="Unenroll"
								onAction={onUnenroll}
							/>
						))}
						</ResponsiveChildren>
					</ContentGroup>
				</ContentRow>
				<ContentRow>
					<ContentGroup name={"Available Cpds"}>
						<ResponsiveChildren minGap={24}>
						{availableCpds.map((cpd, index) => (
							<Card
								key={cpd?.ref?.id ?? index}
								id={cpd?.ref?.id ?? index}
								heading={cpd?.name}
								body={cpd?.description}
								image={`${process.env.PUBLIC_URL}/images/cpd.jpg`}
								actionText="Enroll"
								onAction={onEnroll}
							/>
						))}
						</ResponsiveChildren>
					</ContentGroup>
				</ContentRow>
			</ContentColumn>
			}
		</ContentContainer>
	);
};

export default Student;
